/**
 * AB Test Service - Serviço para gerenciamento de testes A/B
 *
 * Fornece métodos para criar, atualizar, deletar e buscar testes A/B
 */

import { api } from './api';

export interface ABTestVideo {
  id: string;
  name: string;
  thumbnail: string;
  views?: number;
  plays?: number;
  percentage?: number;
}

export interface ABTest {
  id: string;
  id_teste_ab?: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  status?: 'active' | 'paused' | 'completed';
  videos?: ABTestVideo[];
}

export interface CreateABTestPayload {
  name: string;
  id_user: string;
  videosIds: {
    id_video: string;
    trafego: number;
  }[];
  copy?: boolean;
}

export interface UpdateABTestPayload {
  name?: string;
  videosIds?: {
    id_video: string;
    trafego: number;
  }[];
}

export interface ABTestMetrics {
  id_video: string;
  nome_video: string;
  thumbnail: string;
  views: number;
  plays: number;
  play_rate: number;
  avg_watch_time: number;
  completion_rate: number;
  conversions?: number;
  trafego_percentual: number;
}

export const abTestService = {
  /**
   * POST /test-ab/:id_video/copy
   * Cria uma cópia de um vídeo para teste A/B
   */
  copyVideoForTest: async (videoId: string): Promise<any> => {
    const response = await api.post(`/test-ab/${videoId}/copy`);
    return response;
  },

  /**
   * POST /test-ab
   * Cria um novo teste A/B
   */
  createABTest: async (data: CreateABTestPayload): Promise<ABTest> => {
    const response = await api.post('/test-ab', data);
    return response;
  },

  /**
   * GET /test-ab/:id_user
   * Busca todos os testes A/B de um usuário
   */
  getUserABTests: async (userId: string): Promise<ABTest[]> => {
    const response = await api.get(`/test-ab/${userId}`);
    return Array.isArray(response) ? response : [];
  },

  /**
   * PUT /test-ab/:id_teste_ab
   * Atualiza nome ou distribuição de tráfego de um teste A/B
   */
  updateABTest: async (testId: string, data: UpdateABTestPayload): Promise<ABTest> => {
    const response = await api.put(`/test-ab/${testId}`, data);
    return response;
  },

  /**
   * DELETE /test-ab/:id_teste_ab
   * Deleta um teste A/B
   */
  deleteABTest: async (testId: string): Promise<void> => {
    await api.delete(`/test-ab/${testId}`);
  },

  /**
   * GET /test-ab/:id_teste_ab/videos
   * Seleciona um vídeo do teste A/B baseado na distribuição de tráfego
   */
  selectTestVideo: async (testId: string): Promise<any> => {
    const response = await api.get(`/test-ab/${testId}/videos`);
    return response;
  },

  /**
   * GET /test-ab/:id_teste_ab/allvideos
   * Busca todos os vídeos de um teste A/B
   */
  getTestVideos: async (testId: string): Promise<ABTestVideo[]> => {
    const response = await api.get(`/test-ab/${testId}/allvideos`);
    return Array.isArray(response) ? response : [];
  },

  /**
   * GET /test-ab/:IdVideo/teste-ab
   * Busca teste A/B por ID do vídeo
   */
  getTestByVideoId: async (videoId: string): Promise<ABTest | null> => {
    const response = await api.get(`/test-ab/${videoId}/teste-ab`);
    return response;
  },

  /**
   * GET /test-ab/:id_teste_ab/metrics?inicio=YYYY-MM-DD&fim=YYYY-MM-DD
   * Busca métricas de todos os vídeos do teste A/B
   */
  getTestMetrics: async (testId: string, inicio?: string, fim?: string): Promise<ABTestMetrics[]> => {
    const params = new URLSearchParams();
    if (inicio) params.append('inicio', inicio);
    if (fim) params.append('fim', fim);

    const url = `/test-ab/${testId}/metrics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);

    // A API retorna { message, data }
    if (response && response.data) {
      return Array.isArray(response.data) ? response.data : [];
    }
    return Array.isArray(response) ? response : [];
  },
};

export default abTestService;
